/**
 * Edge, layout, theme and grouping commands.
 */
import { childrenOf, createEdge, createNode, requireNode, } from '@orbit-mind/domain';
import { appError, err, ok } from '@orbit-mind/shared-types';
import { command, requireEdge, requireEditable, requireParentId, requirePresent, setOrDrop, touch, } from './shared';
// ---------------------------------------------------------------------------------------------
// edges
// ---------------------------------------------------------------------------------------------
export function validateCreateEdge(doc, payload) {
    if (doc.edges[payload.edgeId] !== undefined) {
        return err(appError('VALIDATION_FAILED', `edge already exists: ${payload.edgeId}`));
    }
    if (payload.sourceNodeId === payload.targetNodeId) {
        return err(appError('VALIDATION_FAILED', 'an edge must not connect a node to itself'));
    }
    const source = requirePresent(doc, payload.sourceNodeId);
    if (!source.ok)
        return source;
    const target = requirePresent(doc, payload.targetNodeId);
    return target.ok ? ok(undefined) : target;
}
export function executeCreateEdge(doc, payload, meta) {
    const edge = createEdge({
        id: payload.edgeId,
        mapId: doc.map.id,
        sourceNodeId: payload.sourceNodeId,
        targetNodeId: payload.targetNodeId,
        now: meta.timestamp,
        ...(payload.label !== undefined ? { label: payload.label } : {}),
    });
    return {
        document: {
            map: touch(doc.map, meta),
            nodes: doc.nodes,
            edges: { ...doc.edges, [edge.id]: edge },
        },
        inverse: command('delete-edge', { edgeId: payload.edgeId }, meta),
    };
}
export function validateDeleteEdge(doc, payload) {
    if (doc.edges[payload.edgeId] === undefined) {
        return err(appError('EDGE_NOT_FOUND', `edge not found: ${payload.edgeId}`));
    }
    return ok(undefined);
}
export function executeDeleteEdge(doc, payload, meta) {
    const edge = requireEdge(doc, payload.edgeId);
    // Rebuilt rather than deleted: a dynamic `delete` de-optimises the object shape, and this runs
    // on every edge removal.
    const edges = Object.fromEntries(Object.entries(doc.edges).filter(([id]) => id !== payload.edgeId));
    return {
        document: { map: touch(doc.map, meta), nodes: doc.nodes, edges },
        // Restores the whole edge, not just its endpoints: label, colour and routing were the
        // user's work too, and an undo that dropped them would be a partial undo.
        inverse: command('restore-edges', { edges: [edge] }, meta),
    };
}
export function validateRestoreEdges(doc, payload) {
    for (const edge of payload.edges) {
        if (doc.nodes[edge.sourceNodeId] === undefined || doc.nodes[edge.targetNodeId] === undefined) {
            return err(appError('NODE_NOT_FOUND', `edge ${edge.id} has an endpoint that no longer exists`));
        }
    }
    return ok(undefined);
}
export function executeRestoreEdges(doc, payload, meta) {
    const edges = { ...doc.edges };
    for (const edge of payload.edges)
        edges[edge.id] = edge;
    return {
        document: { map: touch(doc.map, meta), nodes: doc.nodes, edges },
        inverse: payload.edges.length === 1 && payload.edges[0] !== undefined
            ? command('delete-edge', { edgeId: payload.edges[0].id }, meta)
            : command('restore-edges', { edges: [] }, meta),
    };
}
export function validateUpdateEdge(doc, payload) {
    if (doc.edges[payload.edgeId] === undefined) {
        return err(appError('EDGE_NOT_FOUND', `edge not found: ${payload.edgeId}`));
    }
    return ok(undefined);
}
export function executeUpdateEdge(doc, payload, meta) {
    const edge = requireEdge(doc, payload.edgeId);
    /*
     * Built by keeping what survives rather than by deleting what does not.
     *
     * `delete` on a computed key is the obvious way to write this and the linter is right to refuse
     * it: it mutates an object that was just spread, and on a hot path it de-optimises the shape.
     * Filtering entries says the same thing about the *result* instead of about the steps.
     *
     * Cleared after the merge, so `changes` wins where a caller asked for both — see the payload.
     */
    const dropped = new Set((payload.clear ?? []).filter((field) => !(field in payload.changes)));
    const merged = { ...edge, ...payload.changes };
    const updated = Object.fromEntries(Object.entries(merged).filter(([key]) => !dropped.has(key)));
    updated.updatedAt = meta.timestamp;
    /*
     * The inverse names both halves, because undoing a clear is a set and undoing a set of a field
     * that was absent is a clear.
     *
     * Reading the previous value of every touched field is what makes this work: a field that had
     * nothing in it appears in `clear`, and one that had something appears in `changes`. Reversing
     * a merge alone cannot express "this key was absent", which is the defect this whole change is
     * about — one level up.
     */
    const previous = {};
    const clearAgain = [];
    const touched = new Set([...Object.keys(payload.changes), ...(payload.clear ?? [])]);
    for (const key of touched) {
        const before = edge[key];
        if (before === undefined)
            clearAgain.push(key);
        else
            previous[key] = before;
    }
    return {
        document: {
            map: touch(doc.map, meta),
            nodes: doc.nodes,
            edges: { ...doc.edges, [edge.id]: updated },
        },
        inverse: command('update-edge', {
            edgeId: payload.edgeId,
            changes: previous,
            ...(clearAgain.length === 0 ? {} : { clear: clearAgain }),
        }, meta),
    };
}
// ---------------------------------------------------------------------------------------------
// layout and theme
// ---------------------------------------------------------------------------------------------
export function validateApplyLayout() {
    // Positions for nodes that no longer exist are ignored rather than rejected. A layout runs in
    // a Worker against a snapshot of the document; by the time it returns, a node may have been
    // deleted, and failing the whole pass for that would make layout unreliable under editing.
    return ok(undefined);
}
/**
 * Writes a layout pass into the document.
 *
 * One command for the whole pass. A layout is a single user-visible act — pressing the layout
 * button, or switching engine — and splitting it per node would make undo replay it in slow
 * motion, one node at a time.
 */
export function executeApplyLayout(doc, payload, meta) {
    const nodes = { ...doc.nodes };
    const previous = {};
    for (const [id, position] of Object.entries(payload.positions)) {
        const node = nodes[id];
        if (node === undefined)
            continue;
        // Honours the user's manual placement (principle: an automatic layout must not discard a
        // deliberate choice). The setting is per map, so the whole pass reads it once.
        if (node.pinned === true && doc.map.settings.preserveManualPositions)
            continue;
        Object.assign(previous, {
            [id]: { x: node.x, y: node.y, width: node.width, height: node.height },
        });
        nodes[id] = {
            ...node,
            x: position.x,
            y: position.y,
            ...(position.width !== undefined ? { width: position.width } : {}),
            ...(position.height !== undefined ? { height: position.height } : {}),
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        };
    }
    /*
     * A scoped layout writes the family onto the branch; an unscoped one writes it onto the map.
     *
     * The inverse has to restore *absence* where there was none — a branch that had no layout of
     * its own must go back to having none, not to carrying the map's id, because those are two
     * different documents: the first follows the map when the map's layout changes and the second
     * does not.
     */
    const scope = payload.scopeNodeId;
    if (scope !== undefined) {
        const node = nodes[scope] ?? doc.nodes[scope];
        if (node !== undefined) {
            nodes[scope] = {
                ...node,
                layoutId: payload.layoutId,
                updatedAt: meta.timestamp,
                updatedBy: meta.authorId,
            };
        }
        const had = doc.nodes[scope]?.layoutId;
        return {
            document: { map: touch(doc.map, meta), nodes, edges: doc.edges },
            inverse: had === undefined
                ? command('clear-branch-layout', { nodeId: scope, positions: previous }, meta)
                : command('apply-layout', { layoutId: had, positions: previous, scopeNodeId: scope }, meta),
        };
    }
    const map = { ...touch(doc.map, meta), layoutId: payload.layoutId };
    return {
        document: { map, nodes, edges: doc.edges },
        inverse: command('apply-layout', { layoutId: doc.map.layoutId, positions: previous }, meta),
    };
}
/**
 * Giving a branch the map's layout back.
 *
 * A command of its own because `apply-layout` cannot say "no family" — its `layoutId` is not
 * optional, and it should not be: a layout that could be absent would make every caller ask what
 * an absent one means. This one says exactly one thing, and it is the inverse of the first time a
 * branch was given a family of its own.
 */
export function validateClearBranchLayout(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return ok(undefined);
}
export function executeClearBranchLayout(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    const had = node.layoutId;
    const nodes = { ...doc.nodes };
    const previous = {};
    for (const [id, position] of Object.entries(payload.positions)) {
        const moved = nodes[id];
        if (moved === undefined)
            continue;
        Object.assign(previous, {
            [id]: { x: moved.x, y: moved.y, width: moved.width, height: moved.height },
        });
        nodes[id] = {
            ...moved,
            x: position.x,
            y: position.y,
            ...(position.width === undefined ? {} : { width: position.width }),
            ...(position.height === undefined ? {} : { height: position.height }),
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        };
    }
    nodes[payload.nodeId] = setOrDrop({ ...(nodes[payload.nodeId] ?? node), updatedAt: meta.timestamp, updatedBy: meta.authorId }, 'layoutId', undefined);
    return {
        document: { map: touch(doc.map, meta), nodes, edges: doc.edges },
        inverse: had === undefined
            ? command('clear-branch-layout', { nodeId: payload.nodeId, positions: previous }, meta)
            : command('apply-layout', { layoutId: had, positions: previous, scopeNodeId: payload.nodeId }, meta),
    };
}
/**
 * The longest title a map may carry.
 *
 * The same 500 the entity schema enforces, stated here so a command is refused where it is made
 * rather than accepted into the document and rejected on the way back off the disk — which is a
 * map that saves and will not open.
 */
export const MAX_TITLE_LENGTH = 500;
export function validateRenameMap(_doc, payload) {
    if (payload.title.length > MAX_TITLE_LENGTH) {
        return err(appError('VALIDATION_FAILED', `a title may be at most ${String(MAX_TITLE_LENGTH)} characters`));
    }
    return ok(undefined);
}
export function executeRenameMap(doc, payload, meta) {
    return {
        document: {
            map: { ...touch(doc.map, meta), title: payload.title },
            nodes: doc.nodes,
            edges: doc.edges,
        },
        // The title as it was. An empty one is a real value and round-trips as itself — `setOrDrop`
        // is for fields that can be *absent*, and a map always has a title even when it is ''.
        inverse: command('rename-map', { title: doc.map.title }, meta),
    };
}
export function validateApplyTheme() {
    return ok(undefined);
}
export function executeApplyTheme(doc, payload, meta) {
    return {
        document: {
            map: { ...touch(doc.map, meta), themeId: payload.themeId },
            nodes: doc.nodes,
            edges: doc.edges,
        },
        inverse: command('apply-theme', { themeId: doc.map.themeId }, meta),
    };
}
// ---------------------------------------------------------------------------------------------
// grouping
// ---------------------------------------------------------------------------------------------
export function validateGroupNodes(doc, payload) {
    if (payload.nodeIds.length < 2) {
        return err(appError('VALIDATION_FAILED', 'grouping needs at least two nodes'));
    }
    if (doc.nodes[payload.groupId] !== undefined) {
        return err(appError('VALIDATION_FAILED', `node already exists: ${payload.groupId}`));
    }
    const parents = new Set();
    for (const id of payload.nodeIds) {
        const node = requirePresent(doc, id);
        if (!node.ok)
            return node;
        const editable = requireEditable(node.value);
        if (!editable.ok)
            return editable;
        if (node.value.parentId === null) {
            return err(appError('ROOT_IMMUTABLE', 'the root node cannot be grouped'));
        }
        parents.add(node.value.parentId);
    }
    // Nodes from different branches cannot become siblings under one group without silently
    // reparenting half of them out of their branch, which is not what the user asked for.
    if (parents.size > 1) {
        return err(appError('VALIDATION_FAILED', 'nodes must share a parent to be grouped'));
    }
    return ok(undefined);
}
export function executeGroupNodes(doc, payload, meta) {
    const members = payload.nodeIds
        .map((id) => doc.nodes[id])
        .filter((node) => node !== undefined);
    const first = payload.nodeIds[0];
    if (first === undefined)
        throw new Error('grouping needs at least one node after validation');
    const parentId = requireParentId(requireNode(doc, first));
    const group = createNode({
        id: payload.groupId,
        mapId: doc.map.id,
        parentId,
        order: payload.order,
        now: meta.timestamp,
        by: meta.authorId,
        text: payload.title ?? 'Group',
        type: 'group',
    });
    // Typed explicitly: a computed key in an object literal widens the inferred type to one
    // TypeScript will not let you index again.
    const nodes = { ...doc.nodes, [group.id]: group };
    const orders = {};
    for (const member of members) {
        // Members keep their order keys: they were already ordered relative to each other, and
        // minting new ones would reshuffle a selection the user made deliberately.
        orders[member.id] = member.order;
        nodes[member.id] = {
            ...member,
            parentId: group.id,
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        };
    }
    return {
        document: { map: touch(doc.map, meta), nodes, edges: doc.edges },
        inverse: command('ungroup-nodes', { groupId: group.id, orders: orders }, meta),
    };
}
export function validateUngroupNodes(doc, payload) {
    const group = requirePresent(doc, payload.groupId);
    if (!group.ok)
        return group;
    if (group.value.parentId === null) {
        return err(appError('ROOT_IMMUTABLE', 'the root node is not a group'));
    }
    return requireEditable(group.value);
}
export function executeUngroupNodes(doc, payload, meta) {
    const group = requireNode(doc, payload.groupId);
    const grandparent = requireParentId(group);
    const members = childrenOf(doc, group.id);
    const nodes = Object.fromEntries(Object.entries(doc.nodes).filter(([id]) => id !== group.id));
    for (const member of members) {
        nodes[member.id] = {
            ...member,
            parentId: grandparent,
            order: payload.orders[member.id] ?? member.order,
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        };
    }
    return {
        document: { map: touch(doc.map, meta), nodes, edges: doc.edges },
        inverse: command('group-nodes', {
            groupId: group.id,
            nodeIds: members.map((member) => member.id),
            order: group.order,
            title: group.plainText,
        }, meta),
    };
}
//# sourceMappingURL=document.js.map